import axios from "axios";
import { useAuthStore } from "../store/authStore";
import { keycloakLogout } from "../auth/keycloakClient";

export const API_BASE_URL = __API_BASE_URL__;

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60_000
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  const requestId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  config.headers = config.headers ?? {};
  config.headers["X-Request-ID"] = requestId;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      console.error("API error", error.response.status, error.response.data);
      if (error.response.status === 401) {
        const detail = error.response.data?.detail;
        const code =
          typeof detail === "object" && detail && "code" in detail
            ? String((detail as { code?: unknown }).code ?? "")
            : "";

        // Only clear local session when backend says current auth token is invalid.
        if (code === "not_authenticated" || code === "invalid_token") {
          const state = useAuthStore.getState();
          state.clearAuth("unauthorized");
          if (state.provider === "keycloak") {
            void keycloakLogout();
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

export function extractApiError(error: unknown): string {
  // Axios error shape
  const maybeResponse = (error as { response?: { data?: unknown } })?.response;
  const detail = maybeResponse?.data ?? (error as { detail?: unknown })?.detail ?? error;

  if (detail && typeof detail === "object") {
    // FastAPI style: {"detail": "..."} or {"detail": {"code": "...", "message": "..."}}
    const asAny = detail as Record<string, unknown>;
    if (typeof asAny.detail === "string") {
      return asAny.detail;
    }
    if (asAny.detail && typeof asAny.detail === "object") {
      const inner = asAny.detail as Record<string, unknown>;
      if (typeof inner.message === "string") {
        if (typeof inner.code === "string") {
          return `[${inner.code}] ${inner.message}`;
        }
        return inner.message;
      }
    }
  }

  if (typeof detail === "string") {
    return detail;
  }

  try {
    return JSON.stringify(detail);
  } catch {
    return String(error);
  }
}
